import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import {
  CalendarPlus,
  Ticket,
  CreditCard,
  Vault,
  Banknote,
  RotateCcw,
  AlertTriangle,
  Scale,
  ShieldCheck,
  Ban,
  ScanSearch,
  HandCoins,
  Gavel,
  Globe,
  FileEdit,
  Mail,
  FileCheck,
  Users,
  ClipboardCheck,
  BadgeDollarSign,
  ArrowRight,
} from 'lucide-react';

export default function Organizers() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const sections = [
    { id: 'introduction', title: '1. Introduction', icon: FileCheck },
    { id: 'role-of-platform', title: '2. Role of the Platform', icon: Globe },
    { id: 'account-registration', title: '3. Account Registration & Verification', icon: Users },
    { id: 'event-listings', title: '4. Event Listings', icon: ClipboardCheck },
    { id: 'ticket-sales', title: '5. Ticket Sales & Pricing', icon: Ticket },
    { id: 'fees-payment', title: '6. Fees and Payment Processing', icon: CreditCard },
    { id: 'collection-of-funds', title: '7. Collection of Funds', icon: Vault },
    { id: 'payouts', title: '8. Payouts', icon: Banknote },
    { id: 'refunds-cancellations', title: '9. Refunds and Cancellations', icon: RotateCcw },
    { id: 'chargebacks', title: '10. Chargebacks and Payment Disputes', icon: AlertTriangle },
    { id: 'taxes', title: '11. Taxes', icon: BadgeDollarSign },
    { id: 'compliance', title: '12. Compliance with Laws', icon: Scale },
    { id: 'prohibited', title: '13. Prohibited Activities', icon: Ban },
    { id: 'fraud-prevention', title: '14. Fraud Prevention & Risk Management', icon: ScanSearch },
    { id: 'indemnification', title: '15. Indemnification', icon: HandCoins },
    { id: 'limitation-liability', title: '16. Limitation of Liability', icon: ShieldCheck },
    { id: 'termination', title: '17. Termination', icon: Gavel },
    { id: 'governing-law', title: '18. Governing Law', icon: Scale },
    { id: 'modifications', title: '19. Modifications', icon: FileEdit },
    { id: 'contact-info', title: '20. Contact Information', icon: Mail },
    { id: 'entire-agreement', title: '21. Entire Agreement', icon: FileCheck },
  ];

  const highlights = [
    {
      icon: CalendarPlus,
      title: 'Create & Manage Events',
      desc: 'Tools to create, manage, and sell tickets for your events.',
      color: 'text-primary',
      bg: 'bg-primary/10',
    },
    {
      icon: CreditCard,
      title: 'Secure Payments',
      desc: 'Payments processed through Stripe and Paystack.',
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
    },
    {
      icon: Banknote,
      title: 'Fast Payouts',
      desc: 'Post-event payouts, with accelerated options for trusted Organizers.',
      color: 'text-amber-500',
      bg: 'bg-amber-500/10',
    },
    {
      icon: ShieldCheck,
      title: 'Fraud Protection',
      desc: 'Transaction monitoring and risk management to protect you and your attendees.',
      color: 'text-blue-500',
      bg: 'bg-blue-500/10',
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden" data-testid="organizers-hero">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-secondary/10" />
          <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
            <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-4">For Organizers</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Grow Your Events with{' '}
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                NextGig
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-4">
              The Platform provides tools that allow Organizers to create, manage, and sell tickets for events.
            </p>
            <p className="text-sm text-muted-foreground mb-10">Last Updated: 17 March 2026</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard/events/create">
                <Button size="lg" className="w-full sm:w-auto gap-2 h-12 px-8 text-base" data-testid="hero-create-event-btn">
                  <CalendarPlus className="w-5 h-5" />
                  Create Event
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2 h-12 px-8 text-base" data-testid="hero-get-started-btn">
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Highlights */}
        <section className="py-16 md:py-20 bg-muted/30" data-testid="organizers-highlights">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {highlights.map((h) => {
                const Icon = h.icon;
                return (
                  <div
                    key={h.title}
                    className="p-6 rounded-2xl bg-card border border-border hover:border-primary/40 transition-colors"
                  >
                    <div className={`w-12 h-12 rounded-xl ${h.bg} flex items-center justify-center mb-4 opacity-100`}>
                      <Icon className={`w-6 h-6 ${h.color}`} />
                    </div>
                    <h3 className="font-semibold text-base mb-1 text-foreground">{h.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'hsl(var(--muted-foreground))' }}>{h.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Terms Content */}
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
          {/* Table of Contents */}
          <nav className="mb-12 p-6 bg-muted/30 rounded-lg border border-border" data-testid="organizers-toc">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
              Table of Contents
            </h2>
            <ul className="grid sm:grid-cols-2 gap-2">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Sections */}
          <div className="space-y-10" data-testid="organizers-terms-content">

            {/* 1 */}
            <section id="introduction">
              <h2 className="text-xl md:text-2xl font-bold mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                These Organizer Terms and Conditions ("Organizer Terms") govern your use of NextGig (the "Platform") as an event organizer ("Organizer," "you," or "your").
              </p>
              <p className="text-muted-foreground leading-relaxed">
                By creating, listing, or selling tickets for an event on the Platform, you agree to be legally bound by these Organizer Terms, as well as the Platform's general Terms & Conditions.
              </p>
            </section>

            {/* 2 */}
            <section id="role-of-platform">
              <h2 className="text-xl md:text-2xl font-bold mb-4">2. Role of the Platform</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                The Platform provides tools that allow Organizers to create, manage, and sell tickets for events.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You acknowledge and agree that:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>You are the merchant, seller, and organizer of record for your events.</li>
                <li>The Platform is not the event organizer, promoter, or host.</li>
                <li>The Platform acts solely as a technology and payment facilitation provider.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                You are solely responsible for your events and all related obligations.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-8">2.1 Pricing & Service Fee</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We keep our pricing simple and transparent—so you know exactly what you're paying for. For our non-subscriber organizers
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div className="p-5 rounded-xl bg-muted/40 border border-border">
                  <p className="font-semibold text-foreground mb-2">Platform Fee – 5.5%</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    A 5.5% fee is charged on each ticket sold. This includes all standard bank and payment processing charges—no hidden costs.
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-muted/40 border border-border">
                  <p className="font-semibold text-foreground mb-2">Service Fee – 13.5%</p>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-2">
                    Our 13.5% service fee covers on-ground event support, including:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1 ml-2">
                    <li>Scanning attendees into the venue</li>
                    <li>Wristband distribution</li>
                    <li>On-site ticket sales and assistance</li>
                  </ul>
                </div>
              </div>

              <p className="text-muted-foreground leading-relaxed">
                With NextGig, you get more than just ticketing—you get full support to help your event run smoothly from start to finish.
              </p>
            </section>

            {/* 3 */}
            <section id="account-registration">
              <h2 className="text-xl md:text-2xl font-bold mb-4">3. Account Registration & Verification</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                To use the Platform as an Organizer, you must:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Provide accurate business and personal information.</li>
                <li>Complete identity verification (KYC), if required</li>
                <li>Maintain up-to-date payout and banking details.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4 mb-3">
                We reserve the right to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Approve or reject Organizer accounts.</li>
                <li>Request additional verification documents.</li>
                <li>Suspend accounts that pose fraud or compliance risks.</li>
              </ul>
            </section>

            {/* 4 */}
            <section id="event-listings">
              <h2 className="text-xl md:text-2xl font-bold mb-4">4. Event Listings</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You agree that all event listings:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Are accurate and not misleading.</li>
                <li>Clearly describe the event, pricing, location, and timing</li>
                <li>Comply with all applicable laws and regulations.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4 mb-3">
                You must not list:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Fraudulent or non-existent events</li>
                <li>Events that violate public safety or legal requirements</li>
                <li>Prohibited or restricted activities</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We may remove or suspend any event at our discretion.
              </p>
            </section>

            {/* 5 */}
            <section id="ticket-sales">
              <h2 className="text-xl md:text-2xl font-bold mb-4">5. Ticket Sales & Pricing</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You are responsible for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Setting ticket prices</li>
                <li>Defining ticket types and availability</li>
                <li>Establishing refund policies</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4 mb-3">
                You agree that:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>All pricing must be transparent.</li>
                <li>Any restrictions (e.g., non-transferable tickets) must be clearly disclosed.</li>
              </ul>
            </section>

            {/* 6 */}
            <section id="fees-payment">
              <h2 className="text-xl md:text-2xl font-bold mb-4">6. Fees and Payment Processing</h2>

              <h3 className="text-lg font-semibold mb-3 mt-6">6.1 Platform Fees</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                The Platform charges service fees for ticket sales, which may include:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Per-ticket service fees</li>
                <li>Payment processing fees</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Fees will be disclosed before publishing your event.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">6.2 Payment Processors</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Payments are processed through third-party providers, including Stripe and Paystack.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-3">
                By using the Platform, you agree to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Be bound by the terms of these payment providers.</li>
                <li>Allow the Platform to collect payments on your behalf.</li>
              </ul>
            </section>

            {/* 7 */}
            <section id="collection-of-funds">
              <h2 className="text-xl md:text-2xl font-bold mb-4">7. Collection of Funds (Escrow-Like Holding)</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                All ticket revenue is collected by the Platform and held in a custodial or escrow-like account until payout.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You acknowledge:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Funds may be held until after the event date.</li>
                <li>The Platform may delay payouts for risk management, fraud prevention, or compliance purposes.</li>
                <li>The Platform may impose rolling reserves where necessary.</li>
              </ul>
            </section>

            {/* 8 */}
            <section id="payouts">
              <h2 className="text-xl md:text-2xl font-bold mb-4">8. Payouts</h2>

              <h3 className="text-lg font-semibold mb-3 mt-6">8.1 Payout Schedule</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Payouts will be made according to the Platform's payout policy, which may include:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Post-event payouts (e.g., 1–3 days after event completion)</li>
                <li>Delayed payouts for new or high-risk Organizers</li>
                <li>Accelerated payouts for trusted Organizers</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 mt-6">8.2 Adjustments</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We may adjust payouts to account for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Refunds</li>
                <li>Chargebacks</li>
                <li>Fees or penalties</li>
              </ul>
            </section>

            {/* 9 */}
            <section id="refunds-cancellations">
              <h2 className="text-xl md:text-2xl font-bold mb-4">9. Refunds and Cancellations</h2>

              <h3 className="text-lg font-semibold mb-3 mt-6">9.1 Organizer Responsibility</h3>
              <p className="text-muted-foreground leading-relaxed">
                You are responsible for your refund policy and must clearly communicate it to attendees.
              </p>

              <h3 className="text-lg font-semibold mb-3 mt-6">9.2 Event Cancellation</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                If your event is canceled:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>You must notify attendees promptly.</li>
                <li>You authorize the Platform to issue refunds to attendees.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4 mb-3">
                Refund amounts may be deducted from:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Your pending payouts</li>
                <li>Future payouts</li>
                <li>Your linked payment method if necessary</li>
              </ul>
            </section>

            {/* 10 */}
            <section id="chargebacks">
              <h2 className="text-xl md:text-2xl font-bold mb-4">10. Chargebacks and Payment Disputes</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You agree that:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>You are financially responsible for all chargebacks related to your events.</li>
                <li>Chargeback amounts and associated fees will be deducted from your account.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4 mb-3">
                You authorize the Platform to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Represent you in disputes.</li>
                <li>Submit evidence on your behalf.</li>
                <li>Recover disputed amounts from future payouts.</li>
              </ul>
            </section>

            {/* 11 */}
            <section id="taxes">
              <h2 className="text-xl md:text-2xl font-bold mb-4">11. Taxes</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You are solely responsible for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Determining all applicable taxes (e.g., GST/HST, VAT)</li>
                <li>Collecting and remitting taxes to the appropriate authorities</li>
                <li>Issuing any required tax invoices</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                The Platform is not responsible for your tax obligations.
              </p>
            </section>

            {/* 12 */}
            <section id="compliance">
              <h2 className="text-xl md:text-2xl font-bold mb-4">12. Compliance with Laws</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You agree to comply with all applicable laws, including:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Consumer protection laws</li>
                <li>Ticket resale regulations</li>
                <li>Health and safety requirements</li>
                <li>Licensing and permit requirements</li>
              </ul>
            </section>

            {/* 13 */}
            <section id="prohibited">
              <h2 className="text-xl md:text-2xl font-bold mb-4">13. Prohibited Activities</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You must not:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Sell fraudulent or non-existent tickets.</li>
                <li>Engage in money laundering or illegal financial activity.</li>
                <li>Use bots or manipulation to inflate ticket sales.</li>
                <li>Violate intellectual property rights.</li>
              </ul>
            </section>

            {/* 14 */}
            <section id="fraud-prevention">
              <h2 className="text-xl md:text-2xl font-bold mb-4">14. Fraud Prevention & Risk Management</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                The Platform may:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Monitor transactions for suspicious activity</li>
                <li>Suspend or cancel events deemed high risk.</li>
                <li>Withhold funds during investigations.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We reserve the right to take necessary actions to protect users and the Platform.
              </p>
            </section>

            {/* 15 */}
            <section id="indemnification">
              <h2 className="text-xl md:text-2xl font-bold mb-4">15. Indemnification</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You agree to indemnify and hold harmless NextGig from any claims, damages, or losses arising from:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Your events</li>
                <li>Your breach of these Organizer Terms</li>
                <li>Your violation of laws or third-party rights</li>
              </ul>
            </section>

            {/* 16 */}
            <section id="limitation-liability">
              <h2 className="text-xl md:text-2xl font-bold mb-4">16. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                To the fullest extent permitted by law:
              </p>
              <p className="text-muted-foreground leading-relaxed mb-3">
                The Platform is not liable for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Event outcomes or attendee satisfaction</li>
                <li>Financial losses due to refunds or chargebacks</li>
                <li>Indirect or consequential damages</li>
              </ul>
            </section>

            {/* 17 */}
            <section id="termination">
              <h2 className="text-xl md:text-2xl font-bold mb-4">17. Termination</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We may suspend or terminate your Organizer account if:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>You violate these Terms.</li>
                <li>You engage in fraudulent or illegal activity.</li>
                <li>Your events pose risk to users or the Platform.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Upon termination:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-2">
                <li>Pending payouts may be withheld to cover liabilities.</li>
              </ul>
            </section>

            {/* 18 */}
            <section id="governing-law">
              <h2 className="text-xl md:text-2xl font-bold mb-4">18. Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Organizer Terms shall be governed by the laws of the Province of Ontario and the laws of Canada and Nigeria applicable therein.
              </p>
            </section>

            {/* 19 */}
            <section id="modifications">
              <h2 className="text-xl md:text-2xl font-bold mb-4">19. Modifications</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We may update these Organizer Terms at any time.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Continued use of the Platform constitutes acceptance of the updated Terms.
              </p>
            </section>

            {/* 20 */}
            <section id="contact-info">
              <h2 className="text-xl md:text-2xl font-bold mb-4">20. Contact Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                For questions regarding these Organizer Terms:
              </p>
              <div className="bg-muted/50 rounded-lg p-6 space-y-1">
                <p className="font-semibold text-foreground">NextGig</p>
                <p className="text-muted-foreground">
                  Email:{' '}
                  <a href="mailto:nextgigsuite@gmail.com" className="text-primary hover:underline">
                    nextgigsuite@gmail.com
                  </a>
                </p>
              </div>
            </section>

            {/* 21 */}
            <section id="entire-agreement">
              <h2 className="text-xl md:text-2xl font-bold mb-4">21. Entire Agreement</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Organizer Terms, together with the Platform's general Terms & Conditions, constitute the entire agreement between you and NextGig.
              </p>
            </section>

            {/* Acceptance notice */}
            <div className="mt-12 p-6 bg-primary/5 border border-primary/20 rounded-lg">
              <p className="text-muted-foreground leading-relaxed font-medium">
                By listing an event or selling tickets on NextGig, you acknowledge that you have read, understood, and agree to these Organizer Terms.
              </p>
            </div>
          </div>

          {/* Bottom CTA */}
          <section className="mt-16 md:mt-24 mb-8 text-center" data-testid="organizers-bottom-cta">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to host your next event?</h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
              Create, manage, and sell tickets — all in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard/events/create">
                <Button size="lg" className="w-full sm:w-auto gap-2 h-12 px-8 text-base" data-testid="bottom-create-event-btn">
                  <CalendarPlus className="w-5 h-5" />
                  Create Event
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2 h-12 px-8 text-base" data-testid="bottom-get-started-btn">
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}
